import { useEffect, useMemo, useState } from "react";
import api from "../../api/client";

export default function UserSiteAssignments() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [allowedSites, setAllowedSites] = useState([]); // from org
  const [assignments, setAssignments] = useState({}); // site_id -> access_level
  const [message, setMessage] = useState("");

  const loadUsers = async () => {
    const resp = await api.get("/admin/users/");
    setUsers(resp.data);
  };

  useEffect(() => {
    loadUsers().catch(() => setMessage("Failed to load users"));
  }, []);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)),
    [users, selectedUserId]
  );

  const loadAllowedSites = async (orgId) => {
    const resp = await api.get(`/admin/orgs/${orgId}/sites`);
    setAllowedSites(resp.data);
  };

  const loadUserAssignments = async (userId) => {
    const resp = await api.get(`/admin/users/${userId}/sites`);
    const map = {};
    resp.data.forEach((item) => {
      map[item.site_id] = item.access_level;
    });
    setAssignments(map);
  };

  useEffect(() => {
    if (selectedUser) {
      Promise.all([loadAllowedSites(selectedUser.company_id), loadUserAssignments(selectedUser.id)]).catch(() =>
        setMessage("Failed to load assignments")
      );
    } else {
      setAllowedSites([]);
      setAssignments({});
    }
  }, [selectedUser]);

  const toggleSite = (siteId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      if (next[siteId]) {
        delete next[siteId];
      } else {
        next[siteId] = "read";
      }
      return next;
    });
  };

  const setAccess = (siteId, level) => {
    setAssignments((prev) => ({ ...prev, [siteId]: level }));
  };

  const save = async () => {
    if (!selectedUser) return;
    setMessage("");
    try {
      const payload = {
        sites: Object.entries(assignments).map(([site_id, access_level]) => ({
          site_id: Number(site_id),
          access_level,
        })),
      };
      await api.post(`/admin/users/${selectedUser.id}/sites`, payload);
      setMessage("Saved");
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">User → Sites</h1>
      <div className="flex gap-3 items-center">
        <label className="text-sm text-slate-700">User:</label>
        <select
          className="rounded border px-2 py-2"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
        >
          <option value="">Select</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name || u.user_email} (org {u.company_id})
            </option>
          ))}
        </select>
        <button
          onClick={save}
          className="ml-auto px-4 py-2 bg-slate-900 text-white rounded disabled:opacity-60"
          disabled={!selectedUser}
        >
          Save
        </button>
      </div>
      {message && <div className="text-sm text-slate-600">{message}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {allowedSites.map((s) => {
          const checked = assignments[s.site_id] !== undefined;
          return (
            <div key={s.site_id} className="border rounded px-3 py-2 bg-white shadow-sm flex items-center justify-between gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSite(s.site_id)}
                  disabled={!selectedUser}
                />
                <span className="text-sm text-slate-800">{s.site_name || s.site_id}</span>
              </label>
              {checked && (
                <select
                  className="rounded border px-2 py-1 text-sm"
                  value={assignments[s.site_id]}
                  onChange={(e) => setAccess(s.site_id, e.target.value)}
                >
                  <option value="read">read</option>
                  <option value="write">write</option>
                </select>
              )}
            </div>
          );
        })}
      </div>
      {allowedSites.length === 0 && selectedUser && (
        <div className="text-sm text-slate-500">No sites assigned to this organization yet.</div>
      )}
    </div>
  );
}
